import React from "react";
import CanView from "./CanView.jsx";
/**
 * Reusable TabbedNavigation component
 * @param {Object} props
 * @param {Array} props.tabs - Array of tab objects with { id, label, disabled?, icon?, shortcut?, badgeCount?, badgeClassName? }
 * @param {string} props.activeTab - Currently active tab ID
 * @param {Function} props.onTabChange - Callback when tab changes (tabId) => void
 * @param {string} props.variant - 'default' | 'pills' | 'underline'
 * @param {string} props.size - 'sm' | 'md' | 'lg'
 * @param {string} props.className - Additional CSS classes
 */
const TabbedNavigation = ({
  tabs = [],
  activeTab,
  onTabChange,
  variant = "default",
  size = "md",
  className = "",
}) => {
  const getTabButtonClass = (tab, isActive) => {
    const baseClasses =
      "transition-colors duration-150 focus:outline-none mr-2";

    // Size classes
    const sizeClasses = {
      sm: "px-2 py-1 text-xs",
      md: "px-3 py-1 text-sm",
      lg: "px-4 py-2 text-base",
    };

    // Variant classes
    const variantClasses = {
      default: isActive
        ? "btn-theme-primary"
        : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600",
      pills: isActive
        ? "bg-theme-button-primary hover:bg-theme-button-primary text-theme-button-primary"
        : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600",
      underline: isActive
        ? "text-theme-button-primary border-b-2 border-theme-button-primary"
        : "text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 border-b-2 border-transparent hover:border-slate-300",
    };

    const disabledClasses = tab.disabled
      ? " opacity-50 cursor-not-allowed"
      : "";
    const roundedClasses = variant === "underline" ? "" : " rounded-md";

    return `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]}${disabledClasses}${roundedClasses}`;
  };

  const getContainerClass = () => {
    const baseClasses = "flex";

    if (variant === "underline") {
      return `${baseClasses} border-b border-slate-200 dark:border-slate-700`;
    }

    return `${baseClasses}`;
  };

  return (
    <div className={`${getContainerClass()} ${className}`}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => !tab.disabled && onTabChange(tab.id)}
          disabled={tab.disabled}
          className={getTabButtonClass(tab, activeTab === tab.id)}
        >
          <div className="flex items-center gap-2">
            {tab.icon && <span className="flex-shrink-0">{tab.icon}</span>}
            <span className="font-medium">{tab.label}</span>
            {typeof tab.badgeCount === "number" && tab.badgeCount > 0 && (
              <span
                className={`inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full px-1 text-center text-xs font-semibold leading-none text-white ${
                  tab.badgeClassName || "bg-red-500"
                }`}
              >
                {tab.badgeCount > 99 ? "99+" : tab.badgeCount}
              </span>
            )}
            <CanView webOnly>
              {tab.shortcut && Array.isArray(tab.shortcut) && (
                <div className="flex items-center gap-1">
                  {tab.shortcut.map((key, idx) => (
                    <React.Fragment key={idx}>
                      <kbd
                        className={`inline-block px-1.5 py-0.5 rounded border text-xs font-mono ${
                          activeTab === tab.id
                            ? "text-white/80 border-white/30"
                            : "text-slate-500 border-slate-300 dark:text-slate-400 dark:border-slate-600"
                        }`}
                      >
                        {key}
                      </kbd>
                      {idx < tab.shortcut.length - 1 && (
                        <span
                          className={`text-xs ${
                            activeTab === tab.id
                              ? "text-white/60"
                              : "text-slate-400 dark:text-slate-500"
                          }`}
                        >
                          +
                        </span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </CanView>
          </div>
        </button>
      ))}
    </div>
  );
};

export default TabbedNavigation;
